import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { sessaoLiberada } from '@/features/auth/sessaoLiberada';
import { foraDaProva } from '@/features/relatos/podeRelatar';
import { avisosNesteCelular, nativoComPush, plugin, pushLigadoNoServidor, registrarAparelhoNoPush, removerContaDoPush } from './pushDoAparelho';

/**
 * A NOTIFICAÇÃO NO CELULAR — o lado do aplicativo (1.4; 1.4.1). O servidor manda (`backend/src/services/push.ts`).
 *
 * - Só no aplicativo 1.4.1 ou mais novo (`nativoComPush`), com alguém logado, e só se o SERVIDOR disser que o push está
 *   ligado (`GET /api/notificacoes/push`). Desligado, ou no app antigo, não pergunta nada nem chama o plugin.
 * - Pergunta UMA vez por conta neste aparelho, dizendo quais avisos chegam, e só então pede a permissão do sistema.
 * - O TOQUE: o aviso traz `conta` (o id). É a conta aberta → abre o link. É outra conta guardada neste aparelho ("Trocar de
 *   conta") → "Este aviso é da conta de X. Trocar para ela?". É uma conta que saiu do aparelho → avisa e tira o aparelho
 *   do push dela. Durante a prova o toque não navega.
 * - Com o app aberto o aviso não aparece em cima da tela (`presentationOptions: []`, capacitor.config.ts): o sino mostra.
 */
const PERGUNTADO = (id: string | number) => `push:perguntado:${id}`;

type Toque = { link: string; conta: number; guardada: { userId: number; nome: string } | null };

export function PushNoAplicativo() {
  const { user, entrarComSessao } = useAuth();
  const navigate = useNavigate();
  const cliente = useQueryClient();
  const [perguntar, setPerguntar] = useState(false);
  const [toque, setToque] = useState<Toque | null>(null);
  const [erroDaTroca, setErroDaTroca] = useState<string | null>(null);

  // O toque numa notificação, e o token novo.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let vivo = true;
    const remover: Array<() => void> = [];
    void nativoComPush().then((tem) => {
      if (!tem || !vivo) return;
      const h = plugin().addListener('pushNotificationActionPerformed', async (a) => {
        const d = (a.notification.data ?? {}) as { link?: string; conta?: string };
        const link = d.link && d.link.startsWith('/') ? d.link : '/notificacoes';
        if (!foraDaProva(window.location.pathname)) return; // durante a prova, nada
        const conta = Number(d.conta);
        if (!conta || (user && Number(user.id) === conta)) { navigate(link); return; }
        const { listarContas } = await import('@/features/auth/contas/contasDoAparelho');
        const c = (await listarContas().catch(() => [])).find((x) => x.userId === conta) ?? null;
        setErroDaTroca(null);
        setToque({ link, conta, guardada: c ? { userId: c.userId, nome: c.nome } : null });
        if (!c) void removerContaDoPush(conta);
      });
      const r = plugin().addListener('registration', (t) => { if (user) void registrarAparelhoNoPush(user.id, t.value); });
      remover.push(() => void h.then((x) => x.remove()), () => void r.then((x) => x.remove()));
    });
    return () => { vivo = false; remover.forEach((f) => f()); };
  }, [navigate, user]);

  useEffect(() => {
    if (!user || !sessaoLiberada(user) || !Capacitor.isNativePlatform()) return;
    let jaPerguntou = false;
    try { jaPerguntou = !!localStorage.getItem(PERGUNTADO(user.id)); } catch { jaPerguntou = true; }
    void (async () => {
      if (!(await nativoComPush())) return;
      if (!(await pushLigadoNoServidor())) return;
      if (Capacitor.getPlatform() === 'android') await plugin().createChannel({ id: 'avisos', name: 'Avisos do portal', description: 'Prova nova, correção pendente, PDF pronto', importance: 4, visibility: 0 }).catch(() => undefined);
      const p = await plugin().checkPermissions().catch(() => null);
      if (p?.receive === 'granted') { if (avisosNesteCelular(user.id)) await plugin().register().catch(() => undefined); return; }
      if (!jaPerguntou && p?.receive !== 'denied') setPerguntar(true);
    })().catch(() => undefined);
  // Pela identidade da conta e pela trava, e não pelo objeto: um `user` novo a cada renovação não pergunta de novo.
  }, [user?.id, user?.trocaDeSenhaObrigatoria, user?.termosPendentes]); // eslint-disable-line react-hooks/exhaustive-deps

  const trocarParaAConta = async () => {
    if (!toque?.guardada) return;
    try {
      const { listarContas, trocarPara } = await import('@/features/auth/contas/contasDoAparelho');
      const c = (await listarContas()).find((x) => x.userId === toque.guardada!.userId);
      if (!c) { setToque({ ...toque, guardada: null }); return; }
      const sessao = await trocarPara(c, user ? Number(user.id) : null);
      if (!sessao) return; // cancelou a biometria
      cliente.clear();
      entrarComSessao(sessao);
      const destino = toque.link;
      setToque(null);
      setTimeout(() => navigate(destino), 0);
    } catch (e) { setErroDaTroca((e as Error).message); }
  };

  if (toque) {
    const nome = toque.guardada?.nome.trim().split(/\s+/)[0];
    return (
      <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-4 md:items-center" data-testid="aviso-de-outra-conta">
        <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl" role="dialog" aria-label="Aviso de outra conta">
          {toque.guardada ? (
            <>
              <h2 className="text-base font-semibold text-tinta">Este aviso é da conta de {nome}</h2>
              <p className="mt-2 text-sm text-gray-600">Você está na conta de {user?.name.trim().split(/\s+/)[0] ?? 'outra pessoa'}. Trocar para a conta de {nome} e abrir o aviso?</p>
              {erroDaTroca && <p role="alert" className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{erroDaTroca}</p>}
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setToque(null)} className="min-h-[44px] rounded-md border border-gray-300 px-4 text-sm">Agora não</button>
                <button type="button" onClick={() => void trocarParaAConta()} className="min-h-[44px] rounded-md bg-marca-600 px-4 text-sm font-semibold text-white" data-testid="trocar-pelo-aviso">Trocar para {nome}</button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-base font-semibold text-tinta">Este aviso é de uma conta que saiu deste aparelho</h2>
              <p className="mt-2 text-sm text-gray-600">Ele não abre aqui, e este celular deixa de receber os avisos daquela conta. Para recebê-los de novo, entre nela neste aparelho.</p>
              <div className="mt-4 flex justify-end">
                <button type="button" onClick={() => setToque(null)} className="min-h-[44px] rounded-md bg-marca-600 px-4 text-sm font-semibold text-white">Entendi</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!perguntar || !user) return null;
  const responder = async (sim: boolean) => {
    try { localStorage.setItem(PERGUNTADO(user.id), new Date().toISOString()); } catch { /* pergunta de novo */ }
    setPerguntar(false);
    if (!sim) return;
    const r = await plugin().requestPermissions().catch(() => null);
    if (r?.receive === 'granted') await plugin().register().catch(() => undefined);
  };
  const aluno = user.role === 'STUDENT';
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-4 md:items-center" data-testid="pergunta-do-push">
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl" role="dialog" aria-label="Receber avisos no celular">
        <h2 className="text-base font-semibold text-tinta">Receber avisos no celular?</h2>
        <p className="mt-2 text-sm text-gray-600">{aluno ? 'O portal avisa quando:' : 'O portal avisa quando há:'}</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-gray-700">
          {aluno ? (<>
            <li>uma prova nova abre para você;</li>
            <li>uma prova fecha em 24 h e você ainda não entregou;</li>
            <li>o resultado de uma prova é liberado.</li>
          </>) : (<>
            <li>respostas esperando correção (no máximo um aviso por simulado a cada 6&nbsp;h);</li>
            <li>um simulado seu fechou;</li>
            <li>uma sala sem internet sem resultados enviados;</li>
            <li>o PDF que você pediu pronto.</li>
          </>)}
        </ul>
        <p className="mt-2 text-xs text-gray-500">{aluno ? 'Nada chega durante uma prova, e o aviso não mostra nota.' : 'O aviso mostra o número do simulado, sem nome de aluno.'} No horário de silêncio, o aviso espera o fim dele. Você muda tudo isso em Minha conta.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={() => void responder(false)} className="min-h-[44px] rounded-md border border-gray-300 px-4 text-sm">Agora não</button>
          <button type="button" onClick={() => void responder(true)} className="min-h-[44px] rounded-md bg-marca-600 px-4 text-sm font-semibold text-white" data-testid="aceitar-push">Receber avisos</button>
        </div>
      </div>
    </div>
  );
}
